import { createContext, ReactNode, useState } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { TAuthContext, TAuthUser, TUserType } from '../types/Auth'
import Cookies from 'js-cookie'
import { addDays } from '../functions/dateTime'
import { STAY_COOKIE, USERNAME_COOKIE } from '../constants'
import { TTenantAccount } from '../types/Collaborators'
import { decodeToken } from 'react-jwt'
import env from '../config/env'

type Props = {
  children?: ReactNode
}

const AuthContext = createContext<TAuthContext>({} as TAuthContext)

const AuthProvider = ({ children }: Props) => {
  const userCookie = Cookies.get(USERNAME_COOKIE)
  const stayCookie = Cookies.get(STAY_COOKIE)
  const [user, setAuthUser] = useState<TAuthUser | null>(
    userCookie && userCookie !== '' ? JSON.parse(userCookie) : null
  )
  const [stayConnected, setAuthStayConnected] = useState<boolean>(
    stayCookie ? JSON.parse(stayCookie) : true
  )
  const [userLocal, setUserLocal] = useLocalStorage('user', null)
  const [session, setSession] = useState<TTenantAccount | null>(null)

  const setUser = (newUser: TAuthUser | null) => {
    if (newUser) {
      const expires = !stayConnected
        ? undefined
        : newUser.exp
          ? newUser.exp
          : addDays(1)
      const newUserJson = JSON.stringify(newUser)
      Cookies.set(USERNAME_COOKIE, newUserJson, { expires })
      Cookies.set(USERNAME_COOKIE, newUserJson, {
        expires,
        domain: env.COOKIE_HOST,
      })
      setAuthUser(newUser)
    } else {
      if (session) {
        handleChangeSession(null)
      } else {
        Cookies.remove(USERNAME_COOKIE)
        Cookies.remove(USERNAME_COOKIE, {
          domain: env.COOKIE_HOST,
        })
        setAuthUser(newUser)
      }
    }
  }

  const setStayConnected = (newStay: boolean) => {
    setAuthStayConnected(newStay)
    const expires = addDays(30)
    Cookies.set(STAY_COOKIE, JSON.stringify(newStay), { expires })
  }

  const isUserSysAdmin = () => {
    return user?.UserType === TUserType.SystemAdmin
  }

  if (userLocal) {
    setUser(userLocal as TAuthUser)
    setUserLocal(null)
  }

  const handleChangeSession = (session: TTenantAccount | null) => {
    if (session) {
      const newSessionJson = JSON.stringify(session)
      sessionStorage.setItem('session', newSessionJson)
    } else {
      sessionStorage.removeItem('session')
    }
    setSession(session)
  }

  const getUserLogged = () => {
    let userSession = null
    if (session?.token) {
      userSession = decodeToken<TAuthUser>(session.token)
    }
    return userSession || user
  }

  const getTenantId = () => {
    return getUserLogged()?.TenantId || ''
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        stayConnected,
        session,
        setUser,
        setStayConnected,
        isUserSysAdmin,
        setSession: handleChangeSession,
        getTenantId,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
